// src/components/ConversationItem.js
import React from 'react';
import { UserCircle, Megaphone, Users } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { timeAgo } from '../../utils';

const ConversationItem = ({ conversation, otherUser, currentUserId, onClick }) => {
  const { t } = useTranslation();
  const isUnread = (() => {
    if (!conversation.lastMessageSenderId || conversation.lastMessageSenderId === currentUserId) {
      return false;
    }
    const userReadAt = conversation.readBy?.[currentUserId];
    if (!userReadAt) return true;
    const lastMsgTime = conversation.lastMessageAt?.toMillis?.() || 0;
    const readTime = userReadAt?.toMillis?.() || 0;
    return lastMsgTime > readTime;
  })();

  const convType = conversation.type || 'match';

  return (
    <button
      data-testid="messages-conversation-item"
      onClick={onClick}
      className={`w-full flex items-center gap-3 p-3 sm:p-4 rounded-xl text-left transition-all hover:shadow-md ${
        isUnread
          ? 'bg-white dark:bg-gray-900 border-2 border-rose-200 dark:border-rose-800 shadow-sm'
          : 'bg-white dark:bg-gray-900 border border-gray-100 dark:border-gray-800'
      }`}
    >
      {/* Avatar */}
      <div className="flex-shrink-0 relative">
        {otherUser?.profilePicture ? (
          <img
            src={otherUser.profilePicture}
            alt={otherUser.name || ''}
            className="w-12 h-12 rounded-full object-cover"
          />
        ) : (
          <UserCircle size={48} className="text-gray-300 dark:text-gray-600" />
        )}
        {isUnread && (
          <span className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 sm:w-3 sm:h-3 bg-rose-500 rounded-full ring-2 ring-white dark:ring-gray-900" />
        )}
      </div>

      {/* Contenu */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 min-w-0">
            <span
              className={`text-sm truncate ${
                isUnread
                  ? 'font-bold text-gray-900 dark:text-gray-100'
                  : 'font-semibold text-gray-700 dark:text-gray-300'
              }`}
            >
              {otherUser?.name || 'Utilisateur'}
            </span>
            {convType === 'annonce' && (
              <span
                data-testid="conversation-annonce-badge"
                className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] sm:text-xs font-medium bg-indigo-50 dark:bg-indigo-950/30 text-indigo-600 dark:text-indigo-400 flex-shrink-0"
              >
                <Megaphone size={10} />
                {t('messages.badge.annonce')}
              </span>
            )}
            {convType === 'friend' && (
              <span
                data-testid="conversation-friend-badge"
                className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] sm:text-xs font-medium bg-violet-50 dark:bg-violet-950/30 text-violet-600 dark:text-violet-400 flex-shrink-0"
              >
                <Users size={10} />
                {t('messages.badge.friend')}
              </span>
            )}
          </div>
          <span className="text-xs text-gray-400 dark:text-gray-500 flex-shrink-0">
            {timeAgo(conversation.lastMessageAt, { compact: true })}
          </span>
        </div>
        {convType === 'annonce' && conversation.annonceTitle && (
          <p className="text-xs text-indigo-500 dark:text-indigo-400 truncate mt-0.5">
            {conversation.annonceTitle}
          </p>
        )}
        <p
          className={`text-xs sm:text-sm truncate mt-0.5 ${
            isUnread
              ? 'text-gray-700 dark:text-gray-300 font-medium'
              : 'text-gray-400 dark:text-gray-500'
          }`}
        >
          {conversation.lastMessage || t('messages.startConversation')}
        </p>
      </div>
    </button>
  );
};

export default ConversationItem;
